import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  FileText,
  Send,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Download,
  Loader2,
  BarChart3,
  Users,
  FileCheck,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPublishing = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [readyToPublish, setReadyToPublish] = useState([]);
  const [publishedRecords, setPublishedRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [publishing, setPublishing] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setIsLoading(true);
    try {
      const [dashRes, readyRes, publishedRes] = await Promise.all([
        axios.get(`${API}/admin/publishing/dashboard`),
        axios.get(`${API}/admin/publishing/ready-to-publish`),
        axios.get(`${API}/publish/list?limit=20`)
      ]);
      
      setDashboard(dashRes.data);
      setReadyToPublish(readyRes.data.ready_to_publish || []);
      setPublishedRecords(publishedRes.data.records || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load publishing dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async (sessionId) => {
    setPublishing(prev => ({ ...prev, [sessionId]: true }));
    try {
      const response = await axios.post(`${API}/publish/to-bldr`, {
        session_id: sessionId
      });
      
      if (response.data.success) {
        toast.success('Published to BLDR successfully!');
        loadDashboard();
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const handleBulkPublish = async () => {
    const sessionIds = readyToPublish.map(i => i.id);
    if (sessionIds.length === 0) {
      toast.error('No interviews ready to publish');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/publishing/bulk-publish`, {
        session_ids: sessionIds
      });
      
      toast.success(`Published ${response.data.successful} of ${response.data.total_attempted} interviews`);
      loadDashboard();
    } catch (error) {
      toast.error('Bulk publish failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/documents/list/${sessionId}`);
      const docs = response.data.documents || [];
      const businessPlan = docs.find(d => d.document_type === 'business_plan');
      
      if (businessPlan) {
        setSelectedDocument(businessPlan);
      } else {
        toast.error('No document found. Generating...');
        // Try to assemble
        const assembleRes = await axios.post(`${API}/documents/assemble/business-plan`, {
          session_id: sessionId
        });
        if (assembleRes.data.success) {
          setSelectedDocument({
            document_text: assembleRes.data.document_text,
            document_id: assembleRes.data.document_id
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleRegenerateDocument = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/admin/publishing/regenerate/${sessionId}`);
      if (response.data.success) {
        toast.success('Document regenerated!');
        setSelectedDocument({
          document_text: response.data.document_text,
          document_id: response.data.document_id,
          version: response.data.version
        });
      }
    } catch (error) {
      toast.error('Failed to regenerate document');
    }
  };

  if (isLoading && !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-blue-200 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Admin
              </button>
              <div>
                <h1 className="text-2xl font-bold">Document Publishing</h1>
                <p className="text-blue-200 text-sm">Assemble & publish to BLDR</p>
              </div>
            </div>
            <Button
              onClick={loadDashboard}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Interviews</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.stats.total_interviews}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.stats.completed_interviews}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending Publish</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.stats.pending_publish}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Upload className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Published to BLDR</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.stats.published_to_bldr}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Ready to Publish */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-amber-500" />
                  Ready to Publish ({readyToPublish.length})
                </CardTitle>
                {readyToPublish.length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleBulkPublish}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Publish All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {readyToPublish.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No interviews ready to publish</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {readyToPublish.map((interview) => (
                    <div 
                      key={interview.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {interview.client_name || 'Unnamed Client'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {interview.client_email || 'No email'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Completed: {new Date(interview.completed_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDocument(interview.id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handlePublish(interview.id)}
                            disabled={publishing[interview.id]}
                            className="bg-blue-900 hover:bg-blue-800"
                          >
                            {publishing[interview.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Published Records */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Published to BLDR ({publishedRecords.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {publishedRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No published records yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {publishedRecords.map((record) => (
                    <div 
                      key={record.id}
                      className="p-4 bg-green-50 rounded-lg border border-green-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {record.client_info?.name || 'Unknown'}
                            </p>
                            <Badge className="bg-green-500 text-white text-xs">
                              Published
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {record.client_info?.email || 'No email'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            BLDR ID: {record.id}
                          </p>
                          <p className="text-xs text-gray-400">
                            Published: {new Date(record.published_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDocument(record.source_session_id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Document Preview Modal */}
        {selectedDocument && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Document Preview</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRegenerateDocument(selectedDocument.session_id)}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedDocument(null)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
                {selectedDocument.version && (
                  <Badge className="bg-blue-100 text-blue-800">
                    Version {selectedDocument.version}
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="p-0 overflow-auto max-h-[70vh]">
                <pre className="p-6 text-sm font-mono whitespace-pre-wrap bg-gray-50">
                  {selectedDocument.document_text}
                </pre>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPublishing;
